// Library
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'

// Use
import useMounted from 'use/mounted'
import useForm from 'use/form'

// Helper
import dialog from 'services/dialog'
import { resolveErrorMessage } from 'helpers'

// Components
import IconLock from 'ui/Icons/IconLock'
import Text from 'ui/Text'
import Row from 'ui/Row'
import Col from 'ui/Col'
import Modal from 'ui/Modal'
import Button from 'ui/Button'
import Input from 'ui/Form/Input'

// Api
import { sendForgotPasswordEmail } from 'api/password'

// Validation schema
const validationSchema = yup.object().shape({
  email: yup
  .string()
  .trim()
  .required('The email field is required')
  .email('Invalid email address')
})

// InitialForm
const initialForm = {
  email: '',
}
export const PopupForgotPassword = ({ onCancel, onConfirm, ...props }) => {
  // State
  const [visible, setVisible] = useState(true)
  const [loading, setLoading] = useState(false)
  const isMounted = useMounted()

  // UseFormHook
  const { form, errors, validate, onChange, onBlur, setFieldError } = useForm({
    schema: validationSchema,
    initialForm,
  })

  const onCancelClick = () => {
    // Custom callback
    if (onCancel) {
      return onCancel({
        setVisible,
      })
    }

    setVisible(false)
  }

  const onConfirmClick = async () => {
    try {
      await validate()
    } catch (error) {
      return
    }

    try {
      setLoading(true)
      await sendForgotPasswordEmail(form.email)

      onConfirm()

      if (isMounted.current) {
        setVisible(false)
      }
    } catch (error) {
      setFieldError('email', '')
      if (error.code === 'SIGNIN.ACCOUNT.NOT.FOUND') {
        dialog.alert({
          title: 'User not found in the System.',
          message: 'Please contact Admin.',
        })
        return
      }
      setFieldError('email', await resolveErrorMessage(error))
    } finally {
      if (isMounted.current) {
        setLoading(false)
      }
    }
  }

  const onEnterPress = (e) => {
    if (e.keyCode === 13 && !loading) {
      e.preventDefault()
      onConfirmClick()
    }
  }

  return (
    <Modal show={visible} style={{ textAlign: 'center' }} {...props}>
      <div style={{ textAlign: 'center' }}>
        <IconLock />
      </div>
      <Text size={22} bold paddingBottom={5} color="#000000">
        Forgot your Password?
      </Text>
      <Text size={16} color="#000000" paddingBottom={15}>
      First, let&apos;s find your account by providing your email address
      </Text>

      <Row>
        <Col xs={{ span: 10, offset: 1 }} style={{ padding: '0 5px' }}>
          <Input
            name="email"
            placeholder="Enter email here"
            style={{ textAlign: 'center' }}
            value={form.email}
            errors={errors}
            onChange={onChange}
            onBlur={onBlur}
            onKeyDown={onEnterPress}
          />
        </Col>
      </Row>

      <Row>
        <Col xs={{ span: 5, offset: 1 }}>
          <Button
            block
            variant="outline-primary"
            disabled={loading}
            onClick={onCancelClick}
          >
            Cancel
          </Button>
        </Col>

        <Col xs={{ span: 5 }}>
          <Button block loading={loading} onClick={onConfirmClick}>
            Submit
          </Button>
        </Col>
      </Row>
    </Modal>
  )
}

PopupForgotPassword.propTypes = {
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
}

PopupForgotPassword.defaultProps = {
  onCancel: null,
  onConfirm: null,
}

export default PopupForgotPassword
